import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {Add as AddIcon, ChevronLeft, ChevronRight, Search as SearchIcon} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {FeedbackSnackbar} from "../../shared/ui";
import {formatReais} from "../../shared/utils/formatters";
import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {getPreviaDaLicenca, listLicencas} from "./LicencasService";
import {getStatusColor, getStatusLabel, LICENCA_STATUS_OPTIONS} from "./licencasUtils";
import {dataBR, getMeioLabel, SEM_CICLO_PAGO} from "./assinaturasUtils";
import NovaAssinaturaDialog from "./components/NovaAssinaturaDialog";

const POR_PAGINA = 10;
const celula = {px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', verticalAlign: 'top', fontSize: 14};

// Todas as assinaturas, de 100 em 100: a busca e os filtros são feitos aqui, porque a API não busca por texto.
const listarTodas = async (status) => {
    const itens = [];
    for (let page = 1; page <= 20; page += 1) {
        const response = await listLicencas({page, pageSize: 100, status, sortBy: 'criado_em', order: 'desc'});
        const data = response?.data?.data || {};
        itens.push(...(data.items || []));
        if (!data.pagination?.has_next) break;
    }
    return itens;
};

// A prévia do ciclo de cada linha visível, três de cada vez. Trial, aguardando o cartão e cancelada ficam fora.
const usePrevias = (linhas) => {
    const [previas, setPrevias] = useState({});
    useEffect(() => {
        let ativo = true;
        const fila = linhas.filter((licenca) => !SEM_CICLO_PAGO.includes(licenca.status) && licenca.status !== 'CANCELADA');
        const trabalhar = async () => {
            while (fila.length && ativo) {
                const licenca = fila.shift();
                try {
                    const data = (await getPreviaDaLicenca(licenca.id))?.data?.data;
                    if (ativo) setPrevias((atual) => ({...atual, [licenca.id]: {total: data?.total, emissao: data?.emissao}}));
                } catch (error) {
                    if (ativo) setPrevias((atual) => ({...atual, [licenca.id]: {erro: getBackendErrorMessage(error, 'Prévia indisponível.')}}));
                }
            }
        };
        Promise.all([trabalhar(), trabalhar(), trabalhar()]);
        return () => {
            ativo = false;
        };
    }, [linhas]);
    return previas;
};

const PreviaDaLinha = ({licenca, previa}) => {
    if (licenca.status === 'CANCELADA') return <Typography variant="body2" color="text.secondary">—</Typography>;
    if (SEM_CICLO_PAGO.includes(licenca.status)) return <Typography variant="caption" color="text.secondary">fora do faturamento</Typography>;
    if (!previa) return <CircularProgress size={14} />;
    if (previa.erro) return <Tooltip title={previa.erro}><Typography variant="caption" color="warning.main">indisponível</Typography></Tooltip>;
    return (
        <Box>
            <Typography variant="body2" sx={{fontVariantNumeric: 'tabular-nums'}}>{formatReais(previa.total)}</Typography>
            {previa.emissao ? <Typography variant="caption" color="text.secondary">em {dataBR(previa.emissao)}</Typography> : null}
        </Box>
    );
};

/*
 * Assinaturas (BBF-147, protótipo v2): cada contrato de uma empresa com um plano, com a versão, a situação, o meio e a
 * prévia do ciclo pelo cálculo único. Clicar abre a assinatura. A versão fora de venda e o que está agendado aparecem
 * na linha.
 */
export default function LicencasGestaoPage() {
    const navigate = useNavigate();
    const [estado, setEstado] = useState({carregando: true, erro: '', itens: []});
    const [busca, setBusca] = useState('');
    const [situacao, setSituacao] = useState('');
    const [pagina, setPagina] = useState(0);
    const [novaAberta, setNovaAberta] = useState(false);
    const [feedback, setFeedback] = useState({error: '', success: ''});

    const carregar = useCallback(async () => {
        setEstado((atual) => ({...atual, carregando: true, erro: ''}));
        try {
            setEstado({carregando: false, erro: '', itens: await listarTodas(situacao)});
        } catch (error) {
            setEstado({carregando: false, erro: getBackendErrorMessage(error, 'Não foi possível carregar as assinaturas.'), itens: []});
        }
    }, [situacao]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const filtradas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!termo) return estado.itens;
        return estado.itens.filter((licenca) => [
            licenca.empresa?.nome_fantasia, licenca.empresa?.razao_social, licenca.tenant_id, licenca.versao?.nome_plano, licenca.plano?.sistema?.nome,
        ].some((texto) => (texto || '').toLowerCase().includes(termo)));
    }, [busca, estado.itens]);

    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
    const paginaAtual = Math.min(pagina, totalPaginas - 1);
    const visiveis = useMemo(() => filtradas.slice(paginaAtual * POR_PAGINA, (paginaAtual + 1) * POR_PAGINA), [filtradas, paginaAtual]);
    const previas = usePrevias(visiveis);

    return (
        <Box>
            <Stack direction={{xs: 'column', md: 'row'}} spacing={2} justifyContent="space-between" alignItems={{md: 'center'}} sx={{mb: 2}}>
                <Box>
                    <Typography variant="h5" component="h2" fontWeight={700}>Assinaturas</Typography>
                    <Typography variant="body2" color="text.secondary">Cada contrato de uma empresa com um plano. A assinatura é o tenant, e a empresa recebe uma fatura só com todas.</Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNovaAberta(true)} sx={{flexShrink: 0}}>Nova assinatura</Button>
            </Stack>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{mb: 2}}>
                <TextField size="small" placeholder="Buscar por empresa, tenant, plano ou sistema" value={busca}
                           onChange={(event) => {
                               setBusca(event.target.value);
                               setPagina(0);
                           }}
                           inputProps={{'aria-label': 'Buscar assinaturas'}}
                           InputProps={{startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>}}
                           sx={{flex: 1, maxWidth: {sm: 440}}} />
                <TextField select size="small" value={situacao} sx={{minWidth: 220}}
                           onChange={(event) => {
                               setSituacao(event.target.value);
                               setPagina(0);
                           }}
                           SelectProps={{displayEmpty: true, inputProps: {'aria-label': 'Filtrar por situação'}}}>
                    <MenuItem value="">Todas as situações</MenuItem>
                    {LICENCA_STATUS_OPTIONS.map((item) => <MenuItem key={item} value={item}>{getStatusLabel(item)}</MenuItem>)}
                </TextField>
            </Stack>

            {estado.erro ? <Alert severity="error" sx={{mb: 2}}>{estado.erro}</Alert> : null}

            <Paper variant="outlined" sx={{overflow: 'hidden'}}>
                <Box sx={{overflowX: 'auto'}}>
                    <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', minWidth: 880}}>
                        <thead>
                            <Box component="tr" sx={{'& th': {px: 2, py: 1.25, textAlign: 'left', color: 'text.secondary', fontWeight: 500, fontSize: 13}}}>
                                <th>Assinatura</th>
                                <th>Plano</th>
                                <th>Situação</th>
                                <th>Meio</th>
                                <Box component="th" sx={{textAlign: 'right !important'}}>Prévia do ciclo</Box>
                                <th>Desde</th>
                            </Box>
                        </thead>
                        <tbody>
                            {estado.carregando ? (
                                <tr><Box component="td" colSpan={6} sx={{...celula, py: 5, textAlign: 'center'}}><CircularProgress size={20} /></Box></tr>
                            ) : null}
                            {!estado.carregando && !visiveis.length ? (
                                <tr><Box component="td" colSpan={6} sx={{...celula, py: 5, textAlign: 'center', color: 'text.secondary'}}>Nenhuma assinatura com esses filtros.</Box></tr>
                            ) : null}
                            {!estado.carregando ? visiveis.map((licenca) => (
                                <Box component="tr" key={licenca.id} onClick={() => navigate(`/licencas/${licenca.id}`)} sx={{cursor: 'pointer', '&:hover': {bgcolor: 'action.hover'}}}>
                                    <Box component="td" sx={celula}>
                                        <Typography variant="body2" fontWeight={700}>{licenca.empresa?.nome_fantasia || licenca.empresa?.razao_social || 'Empresa'}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{fontFamily: 'monospace'}}>{licenca.tenant_id}</Typography>
                                    </Box>
                                    <Box component="td" sx={celula}>
                                        <Typography variant="body2">{licenca.versao?.nome_plano || licenca.plano?.nome_plano} v{licenca.versao?.numero}</Typography>
                                        <Typography variant="caption" color="text.secondary">{licenca.plano?.sistema?.nome}</Typography>
                                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{mt: 0.5}}>
                                            {licenca.versao && licenca.versao.situacao !== 'A_VENDA' && licenca.status !== 'CANCELADA' ? <Chip size="small" color="warning" variant="outlined" label="versão fora de venda" /> : null}
                                            {licenca.troca_agendada ? <Chip size="small" color="info" variant="outlined" label="troca agendada" /> : null}
                                            {licenca.cancelamento ? <Chip size="small" color="error" variant="outlined" label="cancelamento agendado" /> : null}
                                        </Stack>
                                    </Box>
                                    <Box component="td" sx={celula}><Chip size="small" label={getStatusLabel(licenca.status)} color={getStatusColor(licenca.status)} variant="outlined" /></Box>
                                    <Box component="td" sx={{...celula, whiteSpace: 'nowrap'}}>{getMeioLabel(licenca.meio_pagamento)}</Box>
                                    <Box component="td" sx={{...celula, textAlign: 'right'}}><PreviaDaLinha licenca={licenca} previa={previas[licenca.id]} /></Box>
                                    <Box component="td" sx={{...celula, whiteSpace: 'nowrap'}}>{dataBR(licenca.validade_inicio)}</Box>
                                </Box>
                            )) : null}
                        </tbody>
                    </Box>
                </Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, py: 1, borderTop: 1, borderColor: 'divider'}}>
                    <Typography variant="caption" color="text.secondary">{filtradas.length} {filtradas.length === 1 ? 'assinatura' : 'assinaturas'}</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <IconButton size="small" aria-label="Página anterior" disabled={paginaAtual === 0} onClick={() => setPagina(paginaAtual - 1)}><ChevronLeft /></IconButton>
                        <Typography variant="caption">Página {paginaAtual + 1} de {totalPaginas}</Typography>
                        <IconButton size="small" aria-label="Próxima página" disabled={paginaAtual >= totalPaginas - 1} onClick={() => setPagina(paginaAtual + 1)}><ChevronRight /></IconButton>
                    </Stack>
                </Stack>
            </Paper>

            <NovaAssinaturaDialog
                aberto={novaAberta}
                onFechar={() => setNovaAberta(false)}
                onCriada={(licenca) => {
                    setNovaAberta(false);
                    if (licenca?.id) navigate(`/licencas/${licenca.id}`);
                    else {
                        setFeedback({error: '', success: 'Assinatura criada.'});
                        carregar();
                    }
                }}
            />
            <FeedbackSnackbar feedback={feedback} onClose={() => setFeedback({error: '', success: ''})} />
        </Box>
    );
}
